import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  useCandidateRegisterMutation,
  useVerifyOtpMutation,
  useResendOtpMutation 
} from '../../store/api/userApiSlice';
import InputField from './InputField';
import PasswordInput from './PasswordInput';
import RadioGroup from './RadioGroup';
import PhoneInput from './PhoneInput';
import './Auth.css';

function SignupForm({ onSwitchToLogin, onClose }) {
  const navigate = useNavigate();
  
  // API mutations
  const [register, { isLoading: isRegistering, error: registerError }] = useCandidateRegisterMutation();
  const [verifyOtp, { isLoading: isVerifying, error: verifyError }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();
  
  // Form steps: 'signup' | 'verify'
  const [step, setStep] = useState('signup');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [otp, setOtp] = useState('');
  
  const [formData, setFormData] = useState({
    userType: '',
    firstName: '',
    lastName: '',
    phone: '',
    location: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [passwordMatch, setPasswordMatch] = useState(null);

  // Handle API errors
  useEffect(() => {
    const error = registerError || verifyError;
    if (error) {
      setGeneralError(error?.data?.message || 'Operation failed. Please try again.');
    }
  }, [registerError, verifyError]);

  const userTypeOptions = [
    { label: 'Student', value: 'student' },
    { label: 'Fresher', value: 'fresher' },
    { label: 'Experienced', value: 'experienced' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (generalError) {
      setGeneralError('');
    }
  };

  const checkPasswordStrength = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);
    const isLongEnough = password.length >= 8;

    if (hasUpperCase && hasNumber && hasSpecialChar && isLongEnough) {
      setPasswordStrength('strong');
    } else {
      setPasswordStrength('weak');
    }
  };

  const checkPasswordMatch = (confirmPass, originalPass) => {
    if (confirmPass === '') {
      setPasswordMatch(null);
      return;
    }
    
    if (originalPass === confirmPass) {
      setPasswordMatch('matched');
    } else {
      setPasswordMatch('not-matched');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userType) newErrors.userType = 'Please select a user type';
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept terms';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Store temporary token and user ID from signup for verification
  const [tempToken, setTempToken] = useState('');
  const [tempUserId, setTempUserId] = useState('');

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');
    
    if (validateForm()) {
      try {
        const signupData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          contactNumber: formData.phone,
          role: 'user'
        };
        
        console.log('Sending signup data:', signupData);
        const result = await register(signupData).unwrap();
        console.log('Signup response:', result);
        
        // Store token and user ID for verification step
        if (result?.token) {
          setTempToken(result.token);
        }
        if (result?.user?._id) {
          setTempUserId(result.user._id);
        }
        
        // Success - move to OTP verification step
        setRegisteredEmail(formData.email);
        setStep('verify');
        setSuccessMessage('Account created! Please enter the OTP sent to your email.');
      } catch (err) {
        console.error('Signup failed:', err);
        if (err?.data?.message) {
          setGeneralError(err.data.message);
        } else if (err?.error) {
          setGeneralError(err.error);
        } else {
          setGeneralError('Signup failed. Please check your information and try again.');
        }
      }
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');
    
    if (!otp || otp.length !== 4) {
      setGeneralError('Please enter a valid 4-digit OTP');
      return;
    }

    try {
      // Use /verify endpoint with token and userId
      const verifyData = { 
        email: registeredEmail, 
        otp: otp,
        userId: tempUserId,
        token: tempToken
      };
      console.log('Verifying OTP with data:', { 
        ...verifyData, 
        token: verifyData.token ? 'PRESENT' : 'MISSING' 
      });
      
      await verifyOtp(verifyData).unwrap();
      
      // Success
      setSuccessMessage('Email verified successfully! You can now login.');
      setTimeout(() => {
        if (onSwitchToLogin) {
          onSwitchToLogin();
        }
      }, 2000);
    } catch (err) {
      console.error('OTP verification failed:', err);
      // Show more detailed error
      if (err?.data?.message) {
        setGeneralError(err.data.message);
      } else if (err?.status) {
        setGeneralError(`Verification failed (Error ${err.status}). Please try again.`);
      } else {
        setGeneralError('OTP verification failed. Please try again.');
      }
    }
  };

  const handleResendOtp = async () => {
    setGeneralError('');
    setSuccessMessage('');
    try {
      await resendOtp({ email: registeredEmail }).unwrap();
      setSuccessMessage('OTP has been resent to your email.');
    } catch (err) {
      console.error('Resend OTP failed:', err);
      setGeneralError(err?.data?.message || 'Failed to resend OTP. Please try again.');
    }
  };

  const isLoading = isRegistering || isVerifying || isResending;

  // OTP Verification Step
  if (step === 'verify') {
    return (
      <form className="signup-form" onSubmit={handleOtpSubmit}>
        <h1 className="form-title">Verify Your Email</h1>

        {generalError && (
          <div className="error-banner">
            {generalError}
            <button 
              type="button" 
              className="error-close"
              onClick={() => setGeneralError('')}
            >
              ×
            </button>
          </div>
        )}

        {successMessage && (
          <div className="success-banner">
            {successMessage}
          </div>
        )}

        <p className="forgot-description">
          We've sent a verification code to <strong>{registeredEmail}</strong>. 
          Please enter the OTP below to verify your account.
        </p>

        <InputField
          label="Enter OTP"
          type="text"
          name="otp"
          placeholder="Enter 4-digit OTP"
          value={otp}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value) && value.length <= 4) {
              setOtp(value);
              setGeneralError('');
            }
          }}
          maxLength={6}
          inputMode="numeric"
        />

        <button type="submit" className="submit-btn" disabled={isLoading}>
          {isLoading ? 'VERIFYING...' : 'VERIFY OTP'}
        </button>

        <p className="signin-link">
          Didn't receive the code?{' '}
          <button type="button" onClick={handleResendOtp} className="link-btn">
            Resend OTP
          </button>
        </p>

        <p className="signin-link">
          <button type="button" onClick={() => setStep('signup')} className="link-btn">
            ← Back to Signup
          </button>
        </p>
      </form>
    );
  }

  // Signup Form Step
  return (
    <form className="signup-form" onSubmit={handleSignupSubmit}>
      <h1 className="form-title">We Welcome You!</h1>

      {generalError && (
        <div className="error-banner">
          {generalError}
          <button 
            type="button" 
            className="error-close"
            onClick={() => setGeneralError('')}
          >
            ×
          </button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          {successMessage}
        </div>
      )}


      <RadioGroup
        label="You are joining as"
        options={userTypeOptions}
        selected={formData.userType}
        onChange={(value) => setFormData({ ...formData, userType: value })}
        required
      />
      {errors.userType && <span className="error-message">{errors.userType}</span>}

      <div className="form-row">
        <InputField
          label="First Name"
          name="firstName"
          placeholder="Eg: John"
          value={formData.firstName}
          onChange={handleChange}
          error={errors.firstName}
          required
        />
        <InputField
          label="Last Name"
          name="lastName"
          placeholder="Eg: Doe"
          value={formData.lastName}
          onChange={handleChange}
          error={errors.lastName}
          required
        />
      </div>

      <div className="form-row">
        <PhoneInput
          label="Phone number"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          error={errors.phone}
        />
        <InputField
          label="Location"
          name="location"
          placeholder="Bangalore"
          value={formData.location}
          onChange={handleChange}
          error={errors.location}
          required
        />
      </div>

      <InputField
        label="Email Address"
        type="email"
        name="email"
        placeholder="Eg: Johndoe@email.com"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        required
      />

      <PasswordInput
        label="Enter Password"
        name="password"
        placeholder="Enter password here"
        value={formData.password}
        onChange={(e) => {
          handleChange(e);
          checkPasswordStrength(e.target.value);
          if (formData.confirmPassword) {
            checkPasswordMatch(formData.confirmPassword, e.target.value);
          }
        }}
        error={errors.password}
        showStrength
        strength={passwordStrength}
        required
      />
      <p className="password-note">
        Note:- Password must contain uppercase, numbers and special characters.
      </p>

      <PasswordInput
        label="Re-enter Password"
        name="confirmPassword"
        placeholder="Re-enter password here"
        value={formData.confirmPassword}
        onChange={(e) => {
          handleChange(e);
          checkPasswordMatch(e.target.value, formData.password);
        }}
        error={errors.confirmPassword}
        required
      />
      
      {!errors.confirmPassword && passwordMatch === 'matched' && (
        <span className="password-matched">Password Matched</span>
      )}
      {!errors.confirmPassword && passwordMatch === 'not-matched' && (
        <span className="password-not-matched">Password Not Matched</span>
      )}

      <label className="checkbox-label">
        <input
          type="checkbox"
          name="acceptTerms"
          checked={formData.acceptTerms}
          onChange={handleChange}
        />
        <span>
          I Accept all the <Link to="/privacy-policy" onClick={onClose}>Terms & Conditions</Link> and <Link to="/privacy-policy" onClick={onClose}>Privacy policies</Link>.
        </span>
      </label>
      {errors.acceptTerms && <span className="error-message">{errors.acceptTerms}</span>}

      <button type="submit" className="submit-btn" disabled={isLoading}>
        {isLoading ? 'SIGNING UP...' : 'SIGN UP'}
      </button>

      <p className="signin-link">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="link-btn">
          Click here to Log in!
        </button>
      </p>
    </form>
  );
}

export default SignupForm;
